import PropTypes from "prop-types";
import React, { useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
} from "reactstrap";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// Router
import { useParams, Link } from "react-router-dom";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";

const AdminIssueDetail = props => {
  const { issueId } = useParams();
  document.title = `Admin | Issue ${issueId}`;

  // ================= ISSUE DATA =================
  const issue = {
    issueId: "ISS-1001",
    title: "Stripe webhook failure",
    description:
      "Stripe webhook events are failing intermittently due to timeout.",
    module: "Billing",
    severity: "High",
    status: "Open",
    reportedBy: "System",
    assignedTo: "Backend Team",
    createdAt: "2025-01-12 10:15 AM",
  };

  // ================= AUDIT LOGS =================
  const auditLogs = [
    {
      id: 1,
      issueId: "ISS-1001",
      user: "System",
      module: "Billing",
      action: "Stripe webhook failed",
      ip: "192.168.1.10",
      date: "2025-01-12 10:15 AM",
      status: "Failed",
    },
    {
      id: 2,
      issueId: "ISS-1001",
      user: "Admin User",
      module: "Billing",
      action: "Retry webhook triggered",
      ip: "192.168.1.11",
      date: "2025-01-12 10:20 AM",
      status: "Success",
    },
    {
      id: 3,
      issueId: "ISS-1001",
      user: "Admin User",
      module: "Billing",
      action: "Invoice marked unpaid",
      ip: "192.168.1.11",
      date: "2025-01-12 10:30 AM",
      status: "Success",
    },
  ];

  // ================= FILTER RELATED LOGS =================
  const relatedLogs = useMemo(() => {
    return auditLogs.filter(log => log.issueId === issueId);
  }, [auditLogs, issueId]);

  // ================= BADGES =================
  const severityColor = {
    Critical: "danger",
    High: "warning",
    Medium: "info",
    Low: "secondary",
  };

  const statusColor = {
    Open: "secondary",
    "In Progress": "primary",
    Resolved: "success",
  };

  const logStatusTemplate = row => (
    <Badge color={row.status === "Success" ? "success" : "danger"}>
      {row.status}
    </Badge>
  );

  // ================= UI =================
  return (
    <div className="page-content">
      <Container fluid>

        <Breadcrumbs
          title={props.t("Admin")}
          breadcrumbItem={props.t("Issue Detail")}
        />

        {/* ================= HEADER ================= */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Issue {issue.issueId}</h4>

          <div className="d-flex gap-2">
            <Button color="success" size="sm">
              Mark Resolved
            </Button>
            <Button color="secondary" size="sm">
              Assign
            </Button>
          </div>
        </div>

        <Row>
          {/* ================= ISSUE DETAILS ================= */}
          <Col xl="4">
            <Card className="mb-4">
              <CardBody>
                <h5 className="mb-3">Issue Details</h5>

                <p className="fw-semibold mb-1">
                  {issue.title}
                </p>
                <p className="text-muted mb-3">
                  {issue.description}
                </p>

                <div className="mb-2">
                  <strong>Module:</strong> {issue.module}
                </div>

                <div className="mb-2">
                  <strong>Severity:</strong>{" "}
                  <Badge color={severityColor[issue.severity]}>
                    {issue.severity}
                  </Badge>
                </div>

                <div className="mb-2">
                  <strong>Status:</strong>{" "}
                  <Badge color={statusColor[issue.status]}>
                    {issue.status}
                  </Badge>
                </div>

                <div className="mb-2">
                  <strong>Reported By:</strong>{" "}
                  {issue.reportedBy}
                </div>

                <div className="mb-2">
                  <strong>Assigned To:</strong>{" "}
                  {issue.assignedTo}
                </div>

                <div className="mb-0">
                  <strong>Created At:</strong>{" "}
                  {issue.createdAt}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* ================= RELATED AUDIT LOGS ================= */}
          <Col xl="8">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Related Audit Logs</h5>

                  <Link
                    to={`/admin/audit-logs?issueId=${issue.issueId}`}
                    className="btn btn-link btn-sm"
                  >
                    View All Logs
                  </Link>
                </div>

                <DataTable
                  value={relatedLogs}
                  responsiveLayout="scroll"
                  emptyMessage="No related logs found"
                >
                  <Column field="date" header="Date & Time" />
                  <Column field="user" header="User" />
                  <Column field="module" header="Module" />
                  <Column field="action" header="Action" />
                  <Column field="ip" header="IP" />
                  <Column
                    field="status"
                    header="Status"
                    body={logStatusTemplate}
                  />
                </DataTable>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

AdminIssueDetail.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(AdminIssueDetail);
